import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress, Autocomplete
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "axios";
import {
  MaterialReactTable,
  type MRT_ColumnDef
} from "material-react-table";
import { useSearchParams } from "react-router-dom";

type OrderResult = {
  orderNumber: string;
  orderStatus: string;
  address: string;
  cityStateZip: string;
  county: string;
  buyerName: string;
  sellerName: string;
  surveyorName: string;
  surveyDate: string | null;
  surveyorCompany: string;
  surveyEasements: string | null;
  settlementDate: string | null;
};

type QueueItem = {
  orderNumber: string;
  status: "pending" | "success" | "error";
  active: boolean;
};

type StatusOption = { value: string; label: string };

const statusOptions: StatusOption[] = [
  { value: "In process", label: "In process" },
  { value: "Transferred", label: "Transferred" },
  { value: "Tentative", label: "Tentative" },
  { value: "Hold", label: "Hold" },
  { value: "Pending", label: "Pending" },
  { value: "Firm", label: "Firm" },
  { value: "Completed", label: "Completed" },
  { value: "Closed", label: "Closed" },
  { value: "Adjourned", label: "Adjourned" },
  { value: "Canceled", label: "Canceled" },
  { value: "Duplicate", label: "Duplicate" },
  { value: "Dormant", label: "Dormant" }
];

const statusColorMap: Record<string, string> = {
  "In process": "#3b49d4",
  Transferred: "#3b49d4",
  Tentative: "#252f85",
  Hold: "#7e60ab",
  Pending: "#552899",
  Firm: "#47a842",
  Completed: "#2a8a25",
  Closed: "#1a5417",
  Adjourned: "#E0a800",
  Canceled: "#FF2929",
  Duplicate: "#AC0000",
  Dormant: "#78909C",
  Default: "#000000"
};

const getStatusColor = (statusType: string) => {
  const color = statusColorMap[statusType] || statusColorMap.Default;
  return { color };
};


const formatUsDate = (value: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  });
};

const downloadCsv = (rows: OrderResult[]) => {
  if (!rows.length) return;

  const headers = [
    "Order Number",
    "Order Status",
    "Address",
    "City State Zip",
    "County",
    "Buyer",
    "Seller",
    "Surveyor",
    "Survey Date",
    "Surveyor Company",
    "Survey Easements",
    "Settlement Date"
  ];

  const escape = (value: any) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csv =
      headers.join(",") +
      "\n" +
      rows
          .map((row) =>
              [
                escape(row.orderNumber),
                escape(row.orderStatus),
                escape(row.address),
                escape(row.cityStateZip),
                escape(row.county),
                escape(row.buyerName),
                escape(row.sellerName),
                escape(row.surveyorName),
                escape(formatUsDate(row.surveyDate)),
                escape(row.surveyorCompany),
                escape(row.surveyEasements),
                escape(formatUsDate(row.settlementDate))
              ].join(",")
          )
          .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "multisite-orders.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const bulkAxios = axios.create({ timeout: 120000 });

const updateClosingDate = async (
    orderNumber: string,
    newClosingDate: string | undefined,
    status: string | undefined

) => {
  try {
    await bulkAxios.post("/proxy/api/orders/UpdateClosingInformation", {
      orderNumber,
      settlementDate: !!newClosingDate ?  new Date(newClosingDate) : undefined,
      status: status
    });
    return { ok: true as const };
  } catch (error) {
    return { ok: false as const, error };
  }
};

export default function MultiSiteOrderInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderNumber = searchParams.get("ordernumber") ?? "";
  const [bulkStatus, setBulkStatus] = useState<StatusOption | undefined>(undefined);


  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [data, setData] = useState<OrderResult[]>([]);
  const [loading, setLoading] = useState(false);

  const [rowSelection, setRowSelection] = useState({});
  const [bulkDate, setBulkDate] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [processing, setProcessing] = useState(false);

  const handleSearch = async () => {
    if (!orderNumber) return;

    setSearchParams({ ordernumber: orderNumber });
    setLoading(true);

    try {
      const response = await axios.get(
          "/api/orders/GetOrderMultiSiteOrderInfos",
          { params: { baseordernumber: orderNumber } }
      );

      const results = Array.isArray(response.data)
          ? response.data
          : [response.data];

      setData(results);
      setRowSelection({});
    } finally {
      setLoading(false);
    }
  };

  const startBulkUpdate = async () => {
    const selectedOrderNumbers = Object.keys(rowSelection).map(
        (rowIndex) => data[Number(rowIndex)].orderNumber
    );

    const uniqueOrderNumbers = Array.from(new Set(selectedOrderNumbers));

    const initialQueue: QueueItem[] = uniqueOrderNumbers.map((o) => ({
      orderNumber: o,
      status: "pending",
      active: false
    }));

    setQueue(initialQueue);
    setProcessing(true);

    for (const item of initialQueue) {
      setQueue((prev) =>
          prev.map((q) =>
              q.orderNumber === item.orderNumber
                  ? { ...q, active: true }
                  : q
          )
      );

      const selectedStatus = bulkStatus?.value; 
      const result = await updateClosingDate(
          item.orderNumber,
          bulkDate,
          selectedStatus
      );

      setQueue((prev) =>
          prev.map((q) =>
              q.orderNumber === item.orderNumber
                  ? {
                    ...q,
                    status: result.ok ? "success" : "error",
                    active: false
                  }
                  : q
          )
      );
    }

    setProcessing(false);
    setDialogOpen(false);
    setQueue([]);
    setBulkDate("");
    await handleSearch();
  };

  useEffect(() => {
    if (initialOrderNumber) {
      handleSearch();
    }
  }, []);

  const columns: MRT_ColumnDef<OrderResult>[] = [
    {
      header: "Order Number",
      accessorKey: "orderNumber",
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return (
            <Link
                href={`https://internal.mcres.com/new-home/${value}`}
                target="_blank"
                underline="hover"
            >
              {value}
            </Link>
        );
      }
    },
    { header: "Order Status", accessorKey: "orderStatus" },
    { header: "Address", accessorKey: "address" },
    {
      header: "Settlement Date",
      accessorKey: "settlementDate",
      Cell: ({ cell }) => formatUsDate(cell.getValue<string | null>())
    },
    { header: "City, State, Zip", accessorKey: "cityStateZip" },
    { header: "County", accessorKey: "county" },
    { header: "Buyer", accessorKey: "buyerName" },
    { header: "Seller", accessorKey: "sellerName" },
    { header: "Surveyor", accessorKey: "surveyorName" },
    {
      header: "Survey Date",
      accessorKey: "surveyDate",
      Cell: ({ cell }) => formatUsDate(cell.getValue<string | null>())
    },
    { header: "Surveyor Company", accessorKey: "surveyorCompany" },
    {
      header: "Survey Easements",
      accessorKey: "surveyEasements",
      Cell: ({ cell }) => cell.getValue<string | null>() ?? ""
    }
  ];

  return (
      <Box p={3}>
        <Typography variant="h5" mb={2}>
          Multi-Site Order Info Lookup
        </Typography>

        <Box display="flex" gap={2} mb={3}>
          <TextField
              label="Order Number"
              size="small"
              sx={{ width: 300 }}
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
          />

          <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!orderNumber || loading}
          >
            Search
          </Button>

          <Button
              variant="outlined"
              disabled={!data.length}
              onClick={() => downloadCsv(data)}
          >
            Download CSV
          </Button>
        </Box>

        <MaterialReactTable
            columns={columns}
            data={data}
            enableRowSelection
            enablePagination={false}
            enableStickyHeader
            state={{ isLoading: loading, rowSelection }}
            onRowSelectionChange={setRowSelection}
            renderTopToolbarCustomActions={() => (
                <Button
                    variant="contained"
                    disabled={Object.keys(rowSelection).length === 0}
                    onClick={() => setDialogOpen(true)}
                >
                  Edit Settlement Date
                </Button>
            )}
        />

        <Dialog open={dialogOpen} fullWidth>
          <DialogTitle>Update Settlement Dates</DialogTitle>

          <DialogContent>
            {!processing ? (
                <Box display="flex" flexDirection="column" gap={2} mt={2}>
                  <TextField
                      type="date"
                      label="Settlement Date"
                      value={bulkDate}
                      onChange={(e) => setBulkDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                  />

                  <Autocomplete
                      disableClearable
                      options={statusOptions}
                      value={bulkStatus}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      onChange={(e, val) => setBulkStatus(val)}
                      renderInput={(params) => (
                          <TextField
                              {...params}
                              label="Status"
                              sx={
                                bulkStatus
                                    ? {
                                      "& .MuiInputBase-input": {
                                        ...getStatusColor(bulkStatus.label)
                                      }
                                    }
                                    : undefined
                              }
                          />

                      )}
                      renderOption={(props, option) => (
                          <li
                              {...props}
                              key={option.value}
                              style={{ ...getStatusColor(option.label) }}
                          >
                            {option.label}
                          </li>
                      )}
                  />
                </Box>
            ) : (

                <List>
                  {queue.map((item) => (
                      <ListItem key={item.orderNumber}>
                        <ListItemIcon>
                          {item.active && <CircularProgress size={20} />}
                          {!item.active && item.status === "success" && (
                              <CheckCircleIcon color="success" />
                          )}
                          {!item.active && item.status === "error" && (
                              <ErrorIcon color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={item.orderNumber} />
                      </ListItem>
                  ))}
                </List>
            )}
          </DialogContent>

          <DialogActions>
            {!processing ? (
                <>
                  <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button
                      variant="contained"
                      disabled={!bulkDate && !bulkStatus}
                      onClick={startBulkUpdate}
                  >
                    Start
                  </Button>
                </>
            ) : (
                <Button disabled>Processing</Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
  );
}
